import React, { memo, useMemo, useRef } from 'react';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import { useDispatch } from 'react-redux';
import { useCurrentWorkspace } from '@hooks/workspace';
import AssetsEmptyState from '@components/Assets/AssetsEmptyState';
import { moveAssetToFolder } from '@redux/actions/assetsAction';
import { AssetsList, FoldersList } from '@components/Assets';
import {
  toggleCampaignFolderCreationModal,
  toggleMoveAssetToPrivateFolder,
  toggleUploadAssetModal
} from '@redux/actions/modalAction';
import { useLayout } from '@layouts/PageLayout';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';
import { useSearchParams } from 'react-router-dom';
import { useDebounced } from '@helpers/useDebounced';
import { useAssetsQuery, useAssetsCountQuery } from '@hooks/queries/assets';
import { CampaignItemDto } from '@api/Api';

interface IAssetsTabProps {
  campaign: CampaignItemDto;
  orderBy: string;
  searchQuery: string | undefined;
  activeTabKey: string;
}

const assetsOrderParams = [
  'createdAt:ASC',
  'createdAt:DESC',
  'modifiedAt:ASC',
  'modifiedAt:DESC',
  'deletedAt:ASC',
  'deletedAt:DESC',
  'openedAt:ASC',
  'openedAt:DESC',
  'order:DESC'
] as const;

export default memo(function AssetsTab({
  campaign,
  searchQuery,
  activeTabKey,
  ...otherProps
}: IAssetsTabProps) {
  const [searchParams] = useSearchParams();
  const { currentView } = useLayout();
  const dispatch = useDispatch();
  const [workspace] = useCurrentWorkspace();

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const orderBy: (typeof assetsOrderParams)[number][] | undefined = useMemo(
    () =>
      otherProps.orderBy &&
      assetsOrderParams.includes(otherProps.orderBy as any)
        ? [otherProps.orderBy as (typeof assetsOrderParams)[number]]
        : undefined,
    [otherProps.orderBy]
  );
  const debouncedSearchQuery = useDebounced(searchQuery, 250);

  const prevCampaignId = useRef(campaign.id);

  const flatten = !!debouncedSearchQuery;
  const assetsQuery = useAssetsQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: false,
      searchQuery: debouncedSearchQuery,
      orderBy
    },
    {
      enabled: activeTabKey === 'assets',
      keepPreviousData: campaign.id === prevCampaignId.current
    }
  );
  const assetsCountQuery = useAssetsCountQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: false,
      searchQuery: debouncedSearchQuery
    },
    {
      enabled: activeTabKey === 'assets',
      keepPreviousData: campaign.id === prevCampaignId.current,
      placeholderData: campaign.assetsCount
    }
  );

  const foldersQuery = useAssetsQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: true,
      searchQuery: debouncedSearchQuery,
      orderBy
    },
    {
      enabled: activeTabKey === 'assets',
      keepPreviousData: campaign.id === prevCampaignId.current
    }
  );
  const foldersCountQuery = useAssetsCountQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: true,
      searchQuery: debouncedSearchQuery
    },
    {
      enabled: activeTabKey === 'assets',
      keepPreviousData: campaign.id === prevCampaignId.current,
      placeholderData: campaign.foldersCount
    }
  );

  if (campaign.id !== prevCampaignId.current) {
    prevCampaignId.current = campaign.id;
  }

  const assetsList = useMemo(() => {
    return assetsQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [assetsQuery.data]);

  const foldersList = useMemo(() => {
    return foldersQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [foldersQuery.data]);

  const assetsCount = assetsCountQuery.data ?? 0;
  const foldersCount = foldersCountQuery.data ?? 0;
  const assetsToFetch = Math.max(0, assetsCount - assetsList.length);
  const foldersToFetch = Math.max(0, foldersCount - foldersList.length);

  const allowUpload = !!campaign.permissions.createAssets && !campaign.demo;
  const showEmptyState = allowUpload || !campaign.foldersCount;

  return (
    <div
      style={{
        paddingRight: 34,
        paddingLeft: 34,
        marginRight: -34,
        marginLeft: -34,
        height: 'calc(100vh - 288px - var(--demo-mode-panel-width, 0px))',
        overflow: 'auto'
      }}
    >
      <div className="campaign_items">
        <button
          type="button"
          className="campaign_items__title"
          disabled={!allowUpload}
          onClick={() => {
            if (!allowUpload) return;
            dispatch(
              toggleCampaignFolderCreationModal({
                entity: 'folder',
                campaignId: campaign.id,
                visible: true
              })
            );
          }}
        >
          {campaign.foldersCount ? 'Folders' : 'Add folder'}{' '}
          {allowUpload && <PlusSvg />}
        </button>

        <InfiniteScroll
          loading={foldersQuery.isFetchingNextPage ? 'more' : false}
          next={foldersQuery.fetchNextPage}
          hasMore={foldersQuery.hasNextPage ?? false}
        >
          <FoldersList
            foldersList={foldersList}
            countToFetch={
              foldersQuery.isLoading || foldersQuery.isFetchingNextPage
                ? foldersToFetch
                : 0
            }
            isCardViewMode={currentView === 'tile'}
            canReorganize={otherProps.orderBy === 'order:DESC'}
            canDropInside={true}
            listLoading={foldersQuery.isLoading}
            onAssetDrop={(assetId, folderId) => {
              const asset = assetsList
                .map((x) => x.node)
                .find((x) => x.id === assetId);
              const folder = foldersList
                .map((x) => x.node)
                .find((x) => x.id === folderId);
              if (!asset?.asset || !folder?.folder) return;
              const { permissions } = asset.asset;
              const canMove =
                (folder.isPrivate && permissions.moveToPrivateFolder) ||
                (!folder.isPrivate && permissions.moveToPublicFolder);
              if (!canMove) return;
              const version = asset.asset.versions[0];
              const onConfirm = () =>
                dispatch(moveAssetToFolder({ assetId, folderId }, true));
              if (folder.isPrivate) {
                dispatch(
                  toggleMoveAssetToPrivateFolder({
                    visible: true,
                    folderName: folder.folder.name,
                    assetsName: [`${version.name}.${version.extension}`],
                    callback: onConfirm
                  })
                );
              } else onConfirm();
            }}
          />
        </InfiniteScroll>

        {assetsCount ? (
          <InfiniteScroll
            loading={assetsQuery.isFetchingNextPage ? 'more' : false}
            next={assetsQuery.fetchNextPage}
            hasMore={assetsQuery.hasNextPage ?? false}
          >
            <button
              type="button"
              className="campaign_items__title"
              onClick={() => {
                if (!allowUpload) return;
                dispatch(
                  toggleUploadAssetModal({
                    visible: true,
                    workspaceId: workspace.id,
                    campaignId: campaign.id
                  })
                );
              }}
            >
              Media {allowUpload && <PlusSvg />}
            </button>
            <AssetsList
              campaign={campaign}
              searchQuery={searchQuery || null}
              assetsList={assetsList}
              displayAssetsCount={assetsCount}
              isTileView={currentView === 'tile'}
              page="campaign"
              countToFetch={assetsQuery.isFetchingNextPage ? assetsToFetch : 0}
              canReorganize={otherProps.orderBy === 'order:DESC'}
              canCombine={true}
              listLoading={assetsQuery.isLoading}
              canShare={campaign.additionalPermissions.canShareExternalLinks}
              isShowCompany={false}
              onRefreshAssetList={assetsQuery.refetch}
            />
          </InfiniteScroll>
        ) : (
          showEmptyState && (
            <AssetsEmptyState campaign={campaign} isSearchEmptyState={false} />
          )
        )}
      </div>
    </div>
  );
});
